import { BoardConfiguration } from '@shared/models/boardConfiguration';
import { useEffect } from 'react';
import { useAppDispatch } from '@shared/store';
import mqtt, { IClientOptions } from 'mqtt';
import {
  isStopPressedChanged,
  stopsChanged,
  journeyProgressChanged,
  lineNameChanged,
  destinationChanged,
  journeyChanged,
  expiresChanged,
  typeChanged,
  regNoChanged,
  vehicleNoChanged,
  xidChanged,
  operatorChanged,
} from '@shared/vehicleSlice';
import {
  JourneyInfo,
  JourneyProgress,
  StopPressed,
} from '../models/protobuf/Journey';
import { logError } from '@shared/utils/errorLogging';
import useQueryParameters from '@shared/hooks/useQueryParameters';

const SUBSCRIBED_TOPICS = [
  '/service/infohub/v1/stopPressed',
  '/service/infohub/v1/journey',
  '/service/infohub/v1/journeyProgress',
  '/service/infohub/v1/expires',
  '/service/infohub/vehicleinfo/type',
  '/service/infohub/vehicleinfo/regno',
  '/service/infohub/vehicleinfo/vehicleno',
  '/service/infohub/vehicleinfo/xid',
  '/service/infohub/vehicleinfo/operator',
] as const;
type SubscribedTopic = (typeof SUBSCRIBED_TOPICS)[number];

export function useMqtt(boards: BoardConfiguration[] | undefined) {
  const dispatch = useAppDispatch();
  const queryParameters = useQueryParameters();

  useEffect(() => {
    if (
      boards === undefined ||
      !boards.some((board) => board.type === 'vehicle')
    ) {
      return;
    }

    const vehicleTopic = queryParameters.get('vehicle');
    const id = queryParameters.get('id');

    const clientOptions: IClientOptions = {
      servers: vehicleTopic
        ? [{ host: 'portal.iovt.vt.net/mqtt_labb', port: 443, protocol: 'wss' }]
        : [{ host: 'vcg.pilotfish.net', port: 8884, protocol: 'wss' }],
      clientId: `gds${id ? `-${id}` : ''}-${Math.random()
        .toString(16)
        .substring(2, 8)}`,
    };

    const client = mqtt.connect(clientOptions);

    // Subscribing to topics individually because otherwise didn't get retained messages for vehicle info
    SUBSCRIBED_TOPICS.forEach((topic) => {
      client.subscribe(vehicleTopic ? `${vehicleTopic}${topic}` : topic);
    });

    function handleJourneyInfo(journeyInfo: JourneyInfo) {
      dispatch(
        journeyChanged({
          gid: journeyInfo?.digest?.journeyId,
          serviceType: journeyInfo?.digest?.journeyServiceType,
          journeyStartTime: journeyInfo?.digest?.journeyStartTime,
        }),
      );
      dispatch(lineNameChanged(journeyInfo?.digest?.lineName));
      dispatch(
        destinationChanged({
          destination: journeyInfo?.digest?.destination,
          via: journeyInfo?.digest?.via,
          destination2: journeyInfo?.digest?.destination2,
        }),
      );
      dispatch(stopsChanged(journeyInfo.stops));
    }

    function handleJourneyProgress(journeyProgress: JourneyProgress) {
      dispatch(
        journeyProgressChanged({
          gid: journeyProgress.journeyId,
          indexInJourney: journeyProgress.indexInJourney,
          status: journeyProgress.status,
        }),
      );
    }

    function handleExpires(expires: string) {
      if (!expires) {
        dispatch(expiresChanged(undefined));
        return;
      }
      const newExpires = Date.parse(expires);
      if (isNaN(newExpires)) {
        logError(`Received invalid expires value: ${expires}`);
        dispatch(expiresChanged(undefined));
      } else {
        dispatch(expiresChanged(newExpires));
      }
    }

    function handleMessage(
      topic: SubscribedTopic,
      message: Buffer<ArrayBufferLike>,
    ) {
      switch (topic) {
        case '/service/infohub/v1/stopPressed':
          dispatch(
            isStopPressedChanged(StopPressed.decode(message).stopPressed),
          );
          break;
        case '/service/infohub/v1/journey':
          handleJourneyInfo(JourneyInfo.decode(message));
          break;
        case '/service/infohub/v1/journeyProgress':
          handleJourneyProgress(JourneyProgress.decode(message));
          break;
        case '/service/infohub/v1/expires':
          handleExpires(message.toString());
          break;
        case '/service/infohub/vehicleinfo/type':
          dispatch(typeChanged(message.toString()));
          break;
        case '/service/infohub/vehicleinfo/regno':
          dispatch(regNoChanged(message.toString()));
          break;
        case '/service/infohub/vehicleinfo/vehicleno':
          dispatch(vehicleNoChanged(message.toString()));
          break;
        case '/service/infohub/vehicleinfo/xid':
          dispatch(xidChanged(message.toString()));
          break;
        case '/service/infohub/vehicleinfo/operator':
          dispatch(operatorChanged(message.toString()));
          break;
      }
    }

    client.on('message', (topic, message) => {
      const topicWithoutVehicle = vehicleTopic
        ? topic.substring(vehicleTopic.length)
        : topic;
      const maybeSubscribedTopic = topicWithoutVehicle as SubscribedTopic;
      const subscribedTopic = SUBSCRIBED_TOPICS.includes(maybeSubscribedTopic)
        ? maybeSubscribedTopic
        : undefined;
      if (!subscribedTopic) {
        logError(`Received message on non-subscribed topic ${topic}`);
        return;
      }
      handleMessage(subscribedTopic, message);
    });

    client.on('error', (error) => {
      logError(error);
    });

    return () => {
      client.end();
    };
  }, [boards, dispatch, queryParameters]);
}
